// ============================================================
// 后台管理布局 — 深色科技风
// ============================================================

import { useState } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { Layout, Menu, Button, Dropdown } from 'antd'
import UserAvatar from '../components/UserAvatar'
import {
  DashboardOutlined,
  UserOutlined,
  FileTextOutlined,
  ShoppingOutlined,
  BellOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  HomeOutlined,
  AccountBookOutlined,
  TransactionOutlined,
  DollarOutlined,
  SettingOutlined,
  ShoppingCartOutlined,
  SafetyCertificateOutlined,
  HistoryOutlined,
} from '@ant-design/icons'
import { useAuthStore } from '../stores/auth.store'

const { Header, Sider, Content } = Layout

// ─── 菜单配置 ───────────────────────────────────────────────

const menuItems = [
  { key: '/admin', icon: <DashboardOutlined />, label: '数据统计' },
  { key: '/admin/tasks', icon: <FileTextOutlined />, label: '任务管理' },
  { key: '/admin/users', icon: <UserOutlined />, label: '用户管理' },
  { key: '/admin/products', icon: <ShoppingOutlined />, label: '商品管理' },
  { key: '/admin/announcements', icon: <BellOutlined />, label: '公告管理' },
  { key: '/admin/points', icon: <TransactionOutlined />, label: '积分管理' },
  { key: '/admin/deposits', icon: <DollarOutlined />, label: '充值审核' },
  { key: '/admin/withdrawals', icon: <AccountBookOutlined />, label: '提现管理' },
  { key: '/admin/exchange-orders', icon: <ShoppingCartOutlined />, label: '兑换订单' },
  { key: '/admin/arbitrations', icon: <SafetyCertificateOutlined />, label: '仲裁管理' },
  { key: '/admin/payment-settings', icon: <SettingOutlined />, label: '支付设置' },
  { key: '/admin/logs', icon: <HistoryOutlined />, label: '操作日志' },
]

// ─── 组件 ───────────────────────────────────────────────────

export default function AdminLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuthStore()
  const [collapsed, setCollapsed] = useState(false)

  const onMenuClick = ({ key }: { key: string }) => {
    navigate(key)
  }

  const userMenuItems = [
    { key: 'home', icon: <HomeOutlined />, label: '返回前台' },
    { type: 'divider' as const },
    { key: 'logout', icon: <LogoutOutlined />, label: '退出登录' },
  ]

  const onUserMenuClick = ({ key }: { key: string }) => {
    if (key === 'home') {
      navigate('/')
    } else if (key === 'logout') {
      logout()
    }
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* 侧边栏 */}
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        style={{
          background: 'linear-gradient(180deg, #1A1A2E 0%, #0F0F1A 100%)',
          borderRight: '1px solid rgba(255, 255, 255, 0.06)',
        }}
      >
        <div
          style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              cursor: 'pointer',
            }}
            onClick={() => navigate('/admin')}
          >
            <img
              src="/logo.svg"
              alt="Logo"
              style={{ width: 32, height: 32, borderRadius: 8 }}
            />
            {!collapsed && (
              <span
                style={{
                  color: '#FFFFFF',
                  fontWeight: 700,
                  fontSize: 16,
                  whiteSpace: 'nowrap',
                }}
              >
                后台管理
              </span>
            )}
          </div>
        </div>

        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={onMenuClick}
          style={{
            background: 'transparent',
            border: 'none',
            marginTop: 8,
          }}
          theme="dark"
        />
      </Sider>

      {/* 主内容区 */}
      <Layout>
        {/* 顶部栏 */}
        <Header
          style={{
            background: 'rgba(15, 15, 26, 0.95)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
            padding: '0 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{ color: '#A0A0B0', fontSize: 18 }}
          />

          <Dropdown
            menu={{ items: userMenuItems, onClick: onUserMenuClick }}
            placement="bottomRight"
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                cursor: 'pointer',
                padding: '6px 12px',
                borderRadius: 8,
              }}
            >
              <UserAvatar avatar={user?.avatar} size={32} />
              <span style={{ color: '#A0A0B0', fontSize: 14 }}>
                {user?.nickname || '管理员'}
              </span>
            </div>
          </Dropdown>
        </Header>

        {/* 内容区 */}
        <Content
          style={{
            padding: 24,
            background: '#0F0F1A',
            minHeight: 'calc(100vh - 64px)',
            overflow: 'auto',
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  )
}
